import { faBullseye, faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colors, styled } from 'assets/theme';
import { IQuote } from 'components/WatchBox/components/CompactView';
import { IWatchboxData } from 'components/WatchBox/WatchBoxItem';
import 'font-awesome/css/font-awesome.css';
import React, { useCallback, useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { subscribeListener, unsubscribeListener } from 'redux/ably/sagas';
import { IVoiceOption } from '../../assets/sounds/widgetAudio';
import { DataContainer, NormalText } from '../../assets/theme/StyledComponents';

// import { Logger } from 'logger';
// const debugLog = Logger.create('WIndex.tsx')

const indexOptions = {
  'ES - 1Day': 'VPMES1D',
  'ES - 1Min': 'VPMES1',
  'ES - 5Min': 'VPMES5',
  'ES - 60Min': 'VPMES60',
  // 'NDX - 1Day': 'VPMNDX1D',
  // 'NDX - 1Min': 'VPMNDX1',
  // 'NDX - 5Min': 'VPMNDX5',
  // 'NDX - 60Min': 'VPMNDX60',
  'NQ - 1Day': 'VPMNQ1D',
  'NQ - 1Min': 'VPMNQ1',
  'NQ - 5Min': 'VPMNQ5',
  'NQ - 60Min': 'VPMNQ60',
  // 'SPX - 1Day': 'VPMSPX1D',
  // 'SPX - 1Min': 'VPMSPX1',
  // 'SPX - 5Min': 'VPMSPX5',
  // 'SPX - 60Min': 'VPMSPX60',
  VPMES1: 'ES - 1Min',
  VPMES1D: 'ES - 1Day',
  VPMES5: 'ES - 5Min',
  VPMES60: 'ES - 60Min',
  // VPMNDX1: 'NDX - 1Min',
  // VPMNDX1D: 'NDX - 1Day',
  // VPMNDX5: 'NDX - 5Min',
  // VPMNDX60: 'NDX - 60Min',
  VPMNQ1: 'NQ - 1Min',
  VPMNQ1D: 'NQ - 1Day',
  VPMNQ5: 'NQ - 5Min',
  VPMNQ60: 'NQ - 60Min',
  // VPMSPX1: 'SPX - 1Min',
  // VPMSPX1D: 'SPX - 1Day',
  // VPMSPX5: 'SPX - 5Min',
  // VPMSPX60: 'SPX - 60Min'
};

function getDecimal(value: number) {
    const data = value - Math.floor(value);
    if (data !== 0) {
        const fdata = Math.floor(value - Math.floor(value)) * 100;
        if (fdata !== 0) {
            return fdata < 10 ? `.0${fdata}` : `.${fdata}`;
        }
    }
    return '.00';
}

interface IWatchIndexProps {
  id: number
  title: string
  voices: IVoiceOption,
  symbol: string
}

export const WatchIndex: React.FunctionComponent<IWatchIndexProps> = ({ id, title, voices, symbol }) => {
  const [indicator, setIndicator] = useState('');
  const [color, setColor] = useState('');
  const [speakerState, setSpeakerState] = useState(true);
  const [activeRally, setActiveRally] = useState('');
  const [quote, setQuote] = useState<IQuote>({
    nc: 0,
    p: 0,
  });
  const [watchboxData, setWatchboxData] = useState<IWatchboxData>();

  useEffect(() => {
    subscribeListener(`sym:${symbol}:tick`, id, ({ data }) => {
      setQuote(data);
    });
    return () => unsubscribeListener(`sym:${symbol}:tick`, id);
  }, [symbol, id]);

  useEffect(() => {
    subscribeListener(`watchbox:${indexOptions[title]}`, id, ({ data }) => {
      setWatchboxData(data);
    });
    return () => unsubscribeListener(`watchbox:${indexOptions[title]}`, id);

  }, [id, title]);

  useEffect(() => {
    if (quote && quote.nc) {
      if (quote.nc > 0) {
        setIndicator('+');
        setColor(colors.accents.good.base);
      } else {
        setIndicator('');
        setColor(colors.accents.bad.base);
      }
    }
  }, [quote]);

  useEffect(() => {
    if (watchboxData) {
      // if (speakerState && activeRally === '' && watchboxData.active_name !== '' && watchboxData.active_time === watchboxData.time) {
      //   if (watchboxData.active_name === 'SELL RALLY') {
      //     voices.sell.play().catch(error => debugLog(error));
      //   } else if (watchboxData.active_name === 'BUY DIP') {
      //     voices.buy.play().catch(error => debugLog(error));
      //   }
      // }
      setActiveRally(watchboxData.active_name || '');
    }
  }, [speakerState, activeRally, watchboxData, voices.buy, voices.sell]);

  const speakerSwitch = useCallback(() => {
    setSpeakerState(s => !s);
  }, []);

  const getWatchBoxRallyItems = (watchboxRallyItemData: IWatchboxData) => {
    const { active_name, active_price, active_tgt, active_stop } = watchboxRallyItemData;
    if (active_name === 'SELL RALLY') {
      return (
        <React.Fragment>
          <WatchBoxRallyItem style={{ backgroundColor: colors.accents.bad.base }}>
            <NormalText>Sell rally</NormalText>
            <NormalText>
              <NumberFormat
                value={active_price}
                displayType="text"
                thousandSeparator={true}
                fixedDecimalScale={true}
                decimalScale={2}
              />
            </NormalText>
          </WatchBoxRallyItem>
          <WatchBoxRallyItem>
            <NormalText>Target</NormalText>
            <NormalText>
              <NumberFormat
                  value={active_tgt}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
            </NormalText>
          </WatchBoxRallyItem>
          <WatchBoxRallyItem>
            <NormalText>Stop out</NormalText>
            <NormalText>
              <NumberFormat
                  value={active_stop}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
            </NormalText>
          </WatchBoxRallyItem>
        </React.Fragment>
      );
    } else if (active_name === 'BUY DIP') {
      return (
        <React.Fragment>
          <WatchBoxRallyItem style={{ backgroundColor: colors.accents.good.base }}>
            <NormalText>Buy dip</NormalText>
            <NormalText>
              <NumberFormat
                value={active_price}
                displayType="text"
                thousandSeparator={true}
                fixedDecimalScale={true}
                decimalScale={2}
              />
            </NormalText>
          </WatchBoxRallyItem>
          <WatchBoxRallyItem>
            <NormalText>Target</NormalText>
            <NormalText>
              <NumberFormat
                  value={active_tgt}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
            </NormalText>
          </WatchBoxRallyItem>
          <WatchBoxRallyItem>
            <NormalText>Stop out</NormalText>
            <NormalText>
              <NumberFormat
                  value={active_stop}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
            </NormalText>
          </WatchBoxRallyItem>
        </React.Fragment>
      );
    }

    return (
      <WatchBoxRallyItem>
        <div style={{ gridColumn: 'span 4', textAlign: 'center', height: '92px', display: 'grid', justifyContent: 'center', alignContent: 'center' }}>
          <NormalText>No Active</NormalText>
          <NormalText>Mean Reversion Trade</NormalText>
        </div>
      </WatchBoxRallyItem>
    );
  };

  if (!watchboxData) { return (
    <WatchBoxRallyItem>
      <div style={{ gridColumn: 'span 4', textAlign: 'center', height: '92px', display: 'grid', justifyContent: 'center', alignContent: 'center' }}>
        <NormalText>No Active</NormalText>
        <NormalText>Mean Reversion Trade</NormalText>
      </div>
    </WatchBoxRallyItem>
  )}
  const { p, nc } = quote
  const { sell_p_price, buy_p_price, sell_p_tgt, buy_p_tgt } = watchboxData
  if (p === undefined || nc === undefined || sell_p_price === undefined || buy_p_price === undefined || sell_p_tgt === undefined || buy_p_tgt === undefined) {
    return <div />
  }
  return (
    <div>
      <PricingContainer>
        <div>
          <LargePriceLabel
            fontColor={color}>
            {Math.floor(p)}
            <span style={{ fontSize: 'small' }}>{getDecimal(p)}</span>
          </LargePriceLabel>
          <div style={{ fontSize: 'small', color }}>
            <NumberFormat
              value={nc}
              prefix={indicator}
              displayType="text"
              thousandSeparator={true}
              fixedDecimalScale={true}
              decimalScale={2}
            />
          </div>
        </div>
        <div onClick={speakerSwitch}>
        {
          speakerState ?
          <FontAwesomeIcon size="2x" icon={faVolumeUp} title="Mute" />
          :
          <FontAwesomeIcon size="2x" icon={faVolumeMute} title="Mute" style={{ marginRight: '4px' }} />
        }
        </div>
      </PricingContainer>
      <WatchBoxRallyTable borderColor={activeRally === 'SELL RALLY' ? colors.accents.bad.base : activeRally === 'BUY DIP' ? colors.accents.good.base : ''}>
      { getWatchBoxRallyItems(watchboxData) }
      </WatchBoxRallyTable>
      <TargetContainter>
        <TargetContainterItem borderRight={true} borderBottom={true}>
          <div>Sell Predict</div>
        </TargetContainterItem>
        <TargetContainterItem borderBottom={true}>
          <div>Buy Predict</div>
        </TargetContainterItem>
        <TargetContainterItem borderRight={true} borderBottom={true} bgColor={activeRally === 'SELL RALLY' ? colors.accents.bad.base : ''}>
          <span>{Math.floor(sell_p_price)}</span>
          <span>{getDecimal(sell_p_price)}</span>
        </TargetContainterItem>
        <TargetContainterItem borderBottom={true} bgColor={activeRally === 'BUY DIP' ? colors.accents.good.base : ''}>
          <span>{Math.floor(buy_p_price)}</span>
          <span>{getDecimal(buy_p_price)}</span>
        </TargetContainterItem>
        <TargetContainterItem borderRight={true}>
          <span><FontAwesomeIcon size="1x" icon={faBullseye} title="Bullseye" /></span>
          <span>{Math.floor(sell_p_tgt)}</span>
          <span>{getDecimal(sell_p_tgt)}</span>
        </TargetContainterItem>
        <TargetContainterItem>
          <span><FontAwesomeIcon size="1x" icon={faBullseye} title="Bullseye" /></span>
          <span>{Math.floor(buy_p_tgt)}</span>
          <span>{getDecimal(buy_p_tgt)}</span>
        </TargetContainterItem>
      </TargetContainter>
    </div>
  );
};

const TargetContainterItem = styled.div<{ borderRight?: boolean, borderBottom?: boolean, bgColor?: string}>`
  border-right: ${({ theme, borderRight }) => (borderRight ? `solid 2px ${theme.core.alternateBackground}` : '')};
  border-bottom: ${({ theme, borderBottom }) => (borderBottom ? `solid 2px ${theme.core.alternateBackground}` : '')};
  color: ${({ theme, bgColor }) => (bgColor ? colors.spectrum.offblack.D3 : theme.core.textColor)};
  background: ${({ bgColor }) => bgColor};
  padding: 5px;
  > span:last-of-type {
    font-size: x-small;
  }
`;

const PricingContainer = styled.div`
  padding: 5px;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr auto;
`;

const WatchBoxRallyTable = styled(DataContainer)<{ borderColor: string}>`
  border-radius: 3px;
  border: 3px solid ${({ theme, borderColor }) => (borderColor !== '' ? borderColor : theme.core.alternateBackground)};
  padding: 4px;
`;

const TargetContainter = styled.div`
  padding: 5px;
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  justify-items: stretch;
`;

const WatchBoxRallyItem = styled(DataContainer)`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  border-radius: 3px;
  padding: 9px;
  margin: 4px;
  background-color: ${({ theme }) => theme.core.alternateBackground};
`;

const LargePriceLabel = styled.span<{ fontColor?: string}>`
  font-size: 1.5em;
  font-weight: 300;
  color: ${({ fontColor, theme }) => fontColor || theme.core.textColor};
`;